import cx from 'classnames';
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import styles from './Sticky.scss';

interface StickyProps {
  appContainerId?: string;
  scrollContainerId?: string;
  usedStickyContainerId: string;
  children: React.ReactNode;

  isActive?: boolean;
  forBottom?: boolean;

  onStickyStateChange?: (isSticky?: boolean) => void;
}

interface StickyState {
  isSticky: boolean;
  top: number;
  left: number;
  width: number;
}

const Sticky = ({
  appContainerId = 'app-container',
  scrollContainerId = 'app',
  usedStickyContainerId,
  children,

  isActive = true,
  forBottom = false,

  onStickyStateChange,
}: StickyProps) => {
  const placeholderRef = useRef<HTMLDivElement | null>(null);
  const childrenRef = useRef<HTMLDivElement | null>(null);

  const appContainerRef = useRef<HTMLElement | null>(null);
  const stickyContainerRef = useRef<HTMLElement | null>(null);
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  const [state, setState] = useState<StickyState>({
    isSticky: false,
    top: 0,
    left: 0,
    width: 0,
  });

  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const getDistanceFromTop = useCallback(() => {
    const stickyContainer = stickyContainerRef.current;
    const scrollContainer = scrollContainerRef.current;

    if (stickyContainer && scrollContainer) {
      return stickyContainer.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top;
    }

    return 0;
  }, []);

  const getDistanceFromBottom = useCallback(() => {
    const stickyContainer = stickyContainerRef.current;
    const scrollContainer = scrollContainerRef.current;

    if (stickyContainer && scrollContainer) {
      return stickyContainer.getBoundingClientRect().bottom - scrollContainer.getBoundingClientRect().bottom;
    }

    return 0;
  }, []);

  const computeIsSticky = useCallback(() => {
    const stickyContainer = stickyContainerRef.current;
    const scrollContainer = scrollContainerRef.current;
    const childrenEl = childrenRef.current;

    if (!stickyContainer || !scrollContainer || !childrenEl) {
      return false;
    }

    if (forBottom) {
      const topBreakpoint = stickyContainer.getBoundingClientRect().top;
      const bodyStyle = window.getComputedStyle(document.body, null);

      const offset =
        parseInt(bodyStyle.paddingBottom, 10) +
        parseInt(bodyStyle.paddingTop, 10) +
        childrenEl.getBoundingClientRect().height;

      return (
        getDistanceFromBottom() > 0 &&
        scrollContainer.scrollTop + scrollContainer.getBoundingClientRect().bottom - offset > topBreakpoint
      );
    }

    const bottomBreakpoint = scrollContainer.getBoundingClientRect().top;
    return getDistanceFromTop() < 0 && stickyContainer.getBoundingClientRect().bottom > bottomBreakpoint;
  }, [forBottom, getDistanceFromBottom, getDistanceFromTop]);

  const handleStickyStateChange = useCallback(
    (nextIsSticky: boolean) => {
      const appContainer = appContainerRef.current;
      const childrenEl = childrenRef.current;

      if (isActive && !forBottom && appContainer && childrenEl) {
        const height = childrenEl.getBoundingClientRect().height;
        appContainer.style.paddingTop = nextIsSticky ? `${height}px` : '0';
      }

      onStickyStateChange?.(nextIsSticky);
    },
    [forBottom, isActive, onStickyStateChange],
  );

  const recomputeState = useCallback(
    (activeOverride?: boolean) => {
      const placeholder = placeholderRef.current;
      const childrenEl = childrenRef.current;
      const scrollContainer = scrollContainerRef.current;

      if (!placeholder || !childrenEl || !scrollContainer) {
        return;
      }

      const nextIsSticky = (Boolean(activeOverride) || forBottom) && computeIsSticky();

      if (nextIsSticky !== stateRef.current.isSticky) {
        handleStickyStateChange(nextIsSticky);
      }

      const { width, left } = placeholder.getBoundingClientRect();

      const nextState: StickyState = {
        isSticky: nextIsSticky,
        width,
        left,
        top: stateRef.current.top,
      };

      if (!forBottom) {
        nextState.top = scrollContainer.getBoundingClientRect().top - childrenEl.getBoundingClientRect().height;
      }

      setState((prev) => {
        if (
          prev.isSticky === nextState.isSticky &&
          (!prev.isSticky ||
            (prev.top === nextState.top && prev.left === nextState.left && prev.width === nextState.width))
        ) {
          return prev;
        }
        return nextState;
      });
    },
    [computeIsSticky, forBottom, handleStickyStateChange],
  );

  const onSmthChanged = useCallback(() => {
    recomputeState(isActive);
  }, [isActive, recomputeState]);

  useLayoutEffect(() => {
    appContainerRef.current = document.getElementById(appContainerId);
    stickyContainerRef.current = document.getElementById(usedStickyContainerId);
    scrollContainerRef.current = document.getElementById(scrollContainerId);

    recomputeState(isActive);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appContainerId, scrollContainerId, usedStickyContainerId]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    if (!scrollContainer) {
      return;
    }

    scrollContainer.addEventListener('scroll', onSmthChanged);
    window.addEventListener('resize', onSmthChanged);

    return () => {
      scrollContainer.removeEventListener('scroll', onSmthChanged);
      window.removeEventListener('resize', onSmthChanged);
    };
  }, [onSmthChanged]);

  useEffect(() => {
    recomputeState(isActive);
  }, [isActive, forBottom, recomputeState]);

  useEffect(() => {
    return () => {
      handleStickyStateChange(false);
    };
  }, [handleStickyStateChange]);

  const { isSticky, left, width, top } = state;

  const { style, placeholderStyle } = useMemo(() => {
    const placeholderStyle: React.CSSProperties = {};
    const style: React.CSSProperties = {};

    if (!isSticky || !childrenRef.current) {
      return { style, placeholderStyle };
    }

    style.left = left;
    style.width = width;

    if (forBottom) {
      placeholderStyle.height = childrenRef.current.getBoundingClientRect().height;
    } else {
      style.top = top;
    }

    return { style, placeholderStyle };
  }, [forBottom, isSticky, left, top, width]);

  const classNameSticky = cx(styles.sticky, {
    [styles.isSticky]: isSticky,
    [styles.forBottom]: forBottom,
    [styles.isActive]: forBottom && isActive,
  });

  const classNameDecor = cx(styles.decor, {
    [styles.isActive]: isSticky && forBottom,
  });

  return (
    <>
      <div ref={placeholderRef} style={placeholderStyle} />
      <div className={classNameSticky} ref={childrenRef} style={style}>
        <div className={classNameDecor} />
        {children}
      </div>
    </>
  );
};

export default memo(Sticky);
